import React from 'react';
import { Checkbox, FormControlLabel, TextField } from '@material-ui/core/';

/**
 * What kind of value an algorithm option holds, and how to edit it.
 *
 * The manifest may declare `type` on an option; where it does not, the type is **inferred from
 * `defaultValue`** — a default of `"True"`/`"False"` means a boolean, a numeric default means a number. That
 * inference is what makes the seven built-in algorithms render correctly without editing a single manifest:
 * every one of their flags (`train`, `deploy`, `transform`, `project`, `project_cluster`, `train_details`,
 * `return_input`, `categorize_target`, `reduce`) already carries a `"True"` or `"False"` default.
 *
 * An explicit `type` always wins, so a flag whose default happens to be empty can still be declared.
 */
export const OPTION_TYPES = ['string', 'boolean', 'number'];

const BOOLEANS = ['true', 'false', 'yes', 'no', 't', 'f'];

export function optionType(opt) {
    if (opt && OPTION_TYPES.includes(opt.type)) return opt.type;
    const def = opt && opt.defaultValue;
    if (def === true || def === false) return 'boolean';
    if (typeof def === 'number') return 'number';
    if (typeof def === 'string' && def.trim()) {
        if (BOOLEANS.includes(def.trim().toLowerCase())) return 'boolean';
        if (/^-?[0-9]+(\.[0-9]+)?$/.test(def.trim())) return 'number';
    }
    return 'string';
}

/**
 * Everything reaching the Python arrives as a **query-string argument**, so it is a string by the time it is
 * parsed there — and the parser is `toBoolean`, which accepts `"true"`, `"yes"`, `"t"`, `"1"`.
 *
 * So a checkbox has to emit the word, not a JavaScript boolean: `String(true)` would give `"true"` (which does
 * work) but `"True"` is what every existing manifest and every log line uses, and matching that keeps a job's
 * saved options readable next to the manifest that describes them.
 */
export const BOOL_TRUE = 'True';
export const BOOL_FALSE = 'False';

export function isTruthyOption(value) {
    return ['true', 'yes', 't', '1'].includes(String(value === undefined ? '' : value).trim().toLowerCase());
}

/**
 * One editor for one option, typed.
 *
 * Shared by the algorithm's own defaults, the model's options and the per-run overrides on the create-job
 * dialog — three places that were each rendering a bare text field, so a boolean looked like free text in all
 * of them and "False" could be typed as "false", "no" or "0" with three different outcomes.
 */
export function OptionField({ opt, value, disabled, helperText, onChange, style }) {
    const type = optionType(opt);
    const help = helperText !== undefined ? helperText : opt.description;

    if (type === 'boolean') {
        return <div style={{ width: 300, margin: 5, ...style }}>
            <FormControlLabel
                label={opt.name}
                control={<Checkbox
                    checked={isTruthyOption(value)}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.checked ? BOOL_TRUE : BOOL_FALSE)} />} />
            {help && <div style={{ fontSize: 12, opacity: 0.7, marginLeft: 32 }}>{help}</div>}
        </div>;
    }

    return <TextField
        style={{ width: 300, margin: 5, ...style }}
        label={opt.name}
        type={type === 'number' ? 'number' : 'text'}
        disabled={disabled}
        helperText={help}
        // Required-but-empty is an error for a string or a number. A boolean is never empty — unchecked is a
        // legitimate answer — which is why that case returns above.
        error={!disabled && isRequired(opt) && (value === '' || value === undefined)}
        value={value === undefined || value === null ? '' : value}
        onChange={(e) => onChange(e.target.value)} />;
}

/** `required` arrives from JSON, so it can be a real boolean or the string "true". */
export function isRequired(opt) {
    return opt && (opt.required === true || String(opt.required).toLowerCase() === 'true');
}

export default OptionField;
